// src/pages/StammdatenPage.jsx
// Browse-Ansicht für Kunden und Stationen, mit Wartungsprotokoll-Anlage.

import { useState } from 'react';
import { useKunden, useKunde } from '../hooks/useKunden';
import { useStationen, useStation } from '../hooks/useStationen';
import { useAuth } from '../hooks/useAuth';
import { C, zurueckStil, Icon, SYMBOL } from './formui';
import { BAUFORM_TEXT, MS_FELD_KURZ } from '../lib/auswahllisten';
import { db, supabase, withTimeout } from '../lib/supabase';
import { useProtokolleByStation, createProtokollForStation } from '../hooks/useProtokoll';
import StationFormPage from './StationFormPage';
import KundeFormPage from './KundeFormPage';
import ProtokollFormPage from './ProtokollFormPage';
import { FotosGalerieStation } from './Fotos';
import { frage } from './Frage';


function Card({ children, style }) {
  return <div style={{ background: C.white, borderRadius: 10, boxShadow: '0 1px 6px rgba(10,22,40,0.06)', ...style }}>{children}</div>;
}

function FieldRow({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div style={{ display: 'flex', gap: 12, padding: '6px 0', borderBottom: '1px solid ' + C.gray100 }}>
      <div style={{ color: C.gray500, fontSize: 14, fontWeight: 600, minWidth: 130 }}>{label}</div>
      <div style={{ color: C.navy, fontSize: 15, flex: 1 }}>{value}</div>
    </div>
  );
}

function TabButton({ active, onClick, children }) {
  return (
    <button onClick={onClick} style={{
      flex: 1, padding: '10px 14px',
      background: active ? C.navy : 'transparent', color: active ? C.white : C.gray500,
      border: 'none', borderBottom: active ? '3px solid ' + C.yellow : '3px solid transparent',
      cursor: 'pointer', fontWeight: 700, fontSize: 14,
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    }}>{children}</button>
  );
}

function SearchBar({ value, onChange, placeholder }) {
  return (
    <div style={{ position: 'relative', marginBottom: 12 }}>
      <input type='text' value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        style={{ width: '100%', padding: '10px 14px 10px 38px', borderRadius: 8, border: '1.5px solid ' + C.gray200, fontSize: 16, outline: 'none', boxSizing: 'border-box', background: C.white }}/>
      <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: C.gray500 }}><Icon pfad={SYMBOL.suche} groesse={18}/></span>
      {value && <button onClick={() => onChange('')} aria-label="Suche leeren" style={{ position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: C.gray500, cursor: 'pointer', minWidth: 44, minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon pfad={SYMBOL.schliessen} groesse={18}/></button>}
    </div>
  );
}

const btnStyle = (enabled) => ({
  padding: '7px 14px', borderRadius: 7, background: enabled ? C.white : C.gray100,
  color: enabled ? C.navy : C.gray300, border: '1px solid ' + (enabled ? C.gray200 : C.gray100),
  cursor: enabled ? 'pointer' : 'not-allowed', fontWeight: 700, fontSize: 14,
});

function Pagination({ page, totalPages, hasNext, hasPrev, onNext, onPrev, count }) {
  if (totalPages <= 1) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', gap: 12 }}>
      <div style={{ fontSize: 14, color: C.gray500 }}>Seite <strong>{page + 1}</strong> von {totalPages} - {count} Eintraege</div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={onPrev} disabled={!hasPrev} style={btnStyle(hasPrev)}>Zurück</button>
        <button onClick={onNext} disabled={!hasNext} style={btnStyle(hasNext)}>Weiter</button>
      </div>
    </div>
  );
}

function KundenList({ search, onSelect }) {
  const k = useKunden({ search });
  return (
    <div>
      {k.error && <div style={{ padding: 12, background: C.redBg, color: C.red, borderRadius: 8, marginBottom: 12 }}>! {k.error}</div>}
      {k.loading ? (
        <div style={{ padding: 32, textAlign: 'center', color: C.gray500 }}>Lade Kunden...</div>
      ) : k.kunden.length === 0 ? (
        <div style={{ padding: 32, textAlign: 'center', color: C.gray500 }}>Keine Kunden gefunden{search ? ' für "' + search + '"' : ''}.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {k.kunden.map(kunde => (
            <button key={kunde.id} onClick={() => onSelect(kunde.id)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: C.white, border: '1px solid ' + C.gray100, borderRadius: 8, cursor: 'pointer', textAlign: 'left' }}>
              <div style={{ background: C.navyMid, color: C.yellow, fontWeight: 800, width: 44, height: 44, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, flexShrink: 0 }}>{kunde.kunden_nr || '-'}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: C.navy, fontWeight: 700, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{kunde.name}</div>
                <div style={{ color: C.gray500, fontSize: 14, marginTop: 2 }}>{[kunde.plz, kunde.ort].filter(Boolean).join(' ') || 'Ohne Adresse'}{kunde.ansprechpartner && ' - ' + kunde.ansprechpartner}</div>
              </div>
              <div style={{ color: C.gray500 }}><Icon pfad={SYMBOL.weiter} groesse={20}/></div>
            </button>
          ))}
        </div>
      )}
      <Pagination page={k.page} totalPages={k.totalPages} hasNext={k.hasNext} hasPrev={k.hasPrev} onNext={k.nextPage} onPrev={k.prevPage} count={k.count}/>
    </div>
  );
}

function StationenList({ search, onSelect }) {
  const s = useStationen({ search });
  return (
    <div>
      {s.error && <div style={{ padding: 12, background: C.redBg, color: C.red, borderRadius: 8, marginBottom: 12 }}>! {s.error}</div>}
      {s.loading ? (
        <div style={{ padding: 32, textAlign: 'center', color: C.gray500 }}>Lade Stationen...</div>
      ) : s.stationen.length === 0 ? (
        <div style={{ padding: 32, textAlign: 'center', color: C.gray500 }}>
          Noch keine Stationen{search ? ' für "' + search + '"' : ''}.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {s.stationen.map(st => (
            <button key={st.id} onClick={() => onSelect(st.id)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: C.white, border: '1px solid ' + C.gray100, borderRadius: 8, cursor: 'pointer', textAlign: 'left' }}>
              <div style={{ background: C.yellowBg, color: C.navy, fontSize: 22, width: 44, height: 44, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon pfad={SYMBOL.station} groesse={20}/></div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: C.navy, fontWeight: 700, fontSize: 14 }}>{st.bezeichnung}</div>
                <div style={{ color: C.gray500, fontSize: 14, marginTop: 2 }}>{st.kunden?.name || '-'}{st.standort_ort && ' - ' + st.standort_ort}</div>
              </div>
              <div style={{ color: C.gray500 }}><Icon pfad={SYMBOL.weiter} groesse={20}/></div>
            </button>
          ))}
        </div>
      )}
      <Pagination page={s.page} totalPages={s.totalPages} hasNext={s.hasNext} hasPrev={s.hasPrev} onNext={() => s.setPage(s.page + 1)} onPrev={() => s.setPage(s.page - 1)} count={s.count}/>
    </div>
  );
}

function KundeDetail({ kundeId, onBack, onSelectStation, onAddStation, onEditKunde }) {
  const { kunde, stationen, loading, error } = useKunde(kundeId);
  if (loading) return <div style={{ padding: 32, textAlign: 'center', color: C.gray500 }}>Lade Kunde...</div>;
  if (error) return <div style={{ padding: 16, background: C.redBg, color: C.red, borderRadius: 8 }}>! {error}</div>;
  if (!kunde) return null;
  return (
    <div>
      <button onClick={onBack} style={{ background: 'none', border: 'none', color: C.navyMid, cursor: 'pointer', fontSize: 15, fontWeight: 700, padding: '6px 0', marginBottom: 12 }}>&lt; Zurück zur Kundenliste</button>
      <Card style={{ padding: 18, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14 }}>
          <div style={{ background: C.navyMid, color: C.yellow, fontWeight: 800, width: 56, height: 56, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15 }}>{kunde.kunden_nr || '-'}</div>
          <div>
            <div style={{ color: C.navy, fontWeight: 800, fontSize: 18 }}>{kunde.name}</div>
            <div style={{ color: C.gray500, fontSize: 14, marginTop: 2 }}>Kundennummer {kunde.kunden_nr || '-'}</div>
          </div>
        </div>
        <FieldRow label='Adresse' value={[kunde.strasse, [kunde.plz, kunde.ort].filter(Boolean).join(' ')].filter(Boolean).join(', ')}/>
        <FieldRow label='Ansprechpartner' value={kunde.ansprechpartner}/>
        <FieldRow label='Position' value={kunde.position}/>
        <FieldRow label='Telefon' value={kunde.telefon}/>
        <FieldRow label='Mobil' value={kunde.mobil}/>
        <FieldRow label='E-Mail' value={kunde.email}/>
        <FieldRow label='Notizen' value={kunde.notizen}/>
        {onEditKunde && (
          <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
            <button onClick={() => onEditKunde(kundeId)} style={{ padding: '8px 14px', background: C.yellow, color: C.navy, border: 'none', borderRadius: 7, cursor: 'pointer', fontWeight: 800, fontSize: 15 }}>Kunde bearbeiten</button>
          </div>
        )}
      </Card>
      <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontWeight: 800, color: C.navy, fontSize: 16 }}>Trafostationen <span style={{ color: C.gray500, fontWeight: 600 }}>({stationen.length})</span></div>
        <button onClick={() => onAddStation && onAddStation(kundeId)} style={{ padding: '8px 14px', background: C.yellow, color: C.navy, border: 'none', borderRadius: 7, cursor: 'pointer', fontWeight: 800, fontSize: 15 }}>+ Neue Station</button>
      </div>
      {stationen.length === 0 ? (
        <Card style={{ padding: 18, textAlign: 'center', color: C.gray500 }}>Bei diesem Kunden ist noch keine Trafostation angelegt.</Card>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {stationen.map(st => (
            <button key={st.id} onClick={() => onSelectStation(st.id)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: C.white, border: '1px solid ' + C.gray100, borderRadius: 8, cursor: 'pointer', textAlign: 'left' }}>
              <div style={{ background: C.yellowBg, color: C.navy, fontSize: 22, width: 40, height: 40, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon pfad={SYMBOL.station} groesse={20}/></div>
              <div style={{ flex: 1 }}>
                <div style={{ color: C.navy, fontWeight: 700, fontSize: 14 }}>{st.bezeichnung}</div>
                <div style={{ color: C.gray500, fontSize: 14, marginTop: 2 }}>{[st.standort_strasse, st.standort_ort].filter(Boolean).join(', ') || 'Ohne Standort'}</div>
              </div>
              <div style={{ color: C.gray500 }}><Icon pfad={SYMBOL.weiter} groesse={20}/></div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ProtokolleListe({ stationId, onOpenProtokoll }) {
  const { protokolle, loading } = useProtokolleByStation(stationId);
  if (loading) return <div style={{ color: C.gray500, fontSize: 15 }}>Lade Protokolle...</div>;
  if (protokolle.length === 0) return <div style={{ color: C.gray500, fontSize: 15, fontStyle: 'italic' }}>Noch keine Wartungsprotokolle für diese Station.</div>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {protokolle.map(p => {
        // Alle Abschluss-Status - vorher nur der alte Wert "abgeschlossen",
        // fertige Protokolle standen hier deshalb als ENTWURF.
        const isAbg = ['abgeschlossen', 'monteur_abgeschlossen', 'buero_freigegeben'].includes(p.status);
        const statusText = p.status === 'buero_freigegeben' ? 'FREIGEGEBEN'
          : isAbg ? 'ABGESCHLOSSEN'
          : ['vorbereitet', 'zugewiesen'].includes(p.status) ? 'GEPLANT' : 'ENTWURF';
        return (
          <button key={p.id} onClick={() => onOpenProtokoll(p.id)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: C.gray100, border: 'none', borderRadius: 8, cursor: 'pointer', textAlign: 'left' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, color: C.navy, fontSize: 15 }}>
                {p.wartungs_datum || 'Ohne Datum'}
                <span style={{ marginLeft: 8, fontSize: 12, fontWeight: 700, color: isAbg ? C.green : C.orange, background: isAbg ? C.greenBg : C.orangeBg, padding: '2px 6px', borderRadius: 4 }}>
                  {statusText}
                </span>
              </div>
              <div style={{ fontSize: 13, color: C.gray500, marginTop: 2 }}>Geändert: {p.geaendert_am?.substring(0, 10) || '-'}</div>
            </div>
            <div style={{ color: C.gray500 }}><Icon pfad={SYMBOL.weiter} groesse={20}/></div>
          </button>
        );
      })}
    </div>
  );
}

function StationDetail({ stationId, onBack, onEdit, onDelete, onNeueWartung, onOpenProtokoll, isAdmin }) {
  const s = useStation(stationId);
  if (s.loading) return <div style={{ padding: 32, textAlign: 'center', color: C.gray500 }}>Lade Station...</div>;
  if (s.error) return <div style={{ padding: 16, background: C.redBg, color: C.red, borderRadius: 8 }}>! {s.error}</div>;
  if (!s.station) return null;
  const st = s.station;
  const handleDelete = () => {
    if (!window.confirm('Station "' + st.bezeichnung + '" wirklich löschen? Wird archiviert.')) return;
    if (onDelete) onDelete(stationId);
  };
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: C.navyMid, cursor: 'pointer', fontSize: 15, fontWeight: 700, padding: '6px 0' }}>&lt; Zurück</button>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={() => onNeueWartung(stationId)} style={{ padding: '8px 16px', background: C.green, color: C.white, border: 'none', borderRadius: 7, cursor: 'pointer', fontWeight: 800, fontSize: 15 }}>+ Neue Wartung</button>
          {onEdit && <button onClick={() => onEdit(stationId)} style={{ padding: '8px 14px', background: C.yellow, color: C.navy, border: 'none', borderRadius: 7, cursor: 'pointer', fontWeight: 800, fontSize: 15 }}>Bearbeiten</button>}
          {isAdmin && onDelete && <button onClick={handleDelete} title="Station archivieren" aria-label="Station archivieren" style={{ padding: '8px 12px', background: 'none', color: C.gray500, border: '1px solid ' + C.gray200, borderRadius: 7, cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>🗑️</button>}
        </div>
      </div>
      <Card style={{ padding: 18, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14 }}>
          <div style={{ background: C.yellowBg, color: C.navy, fontSize: 26, width: 56, height: 56, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon pfad={SYMBOL.station} groesse={26}/></div>
          <div>
            <div style={{ color: C.navy, fontWeight: 800, fontSize: 18 }}>{st.bezeichnung}</div>
            <div style={{ color: C.gray500, fontSize: 14, marginTop: 2 }}>{st.kunden?.name || ''} - {st.stations_nr || 'ohne Stationsnummer'}</div>
          </div>
        </div>
        <FieldRow label='Standort' value={[st.standort_strasse, [st.standort_plz, st.standort_ort].filter(Boolean).join(' ')].filter(Boolean).join(', ')}/>
        <FieldRow label='Bauform' value={BAUFORM_TEXT[s.config?.stations_typ || st.typ] || st.typ}/>
        <FieldRow label='Baujahr' value={st.baujahr}/>
        <FieldRow label='Wartungs-Zyklus' value={st.wartungs_zyklus}/>
        <FieldRow label='Letzte Wartung' value={st.letzte_wartung}/>
        <FieldRow label='GPS' value={st.gps_lat && st.gps_lng ? st.gps_lat + ', ' + st.gps_lng : null}/>
        <FieldRow label='Notizen' value={st.notizen}/>
      </Card>

      <Card style={{ padding: 18, marginBottom: 16 }}>
        <div style={{ fontWeight: 800, color: C.navy, marginBottom: 10, fontSize: 15 }}>Wartungsprotokolle</div>
        <ProtokolleListe stationId={stationId} onOpenProtokoll={onOpenProtokoll}/>
      </Card>

      <FotosGalerieStation stationId={stationId} stationsBezeichnung={st.bezeichnung}/>

      {s.config && (s.config.stations_hersteller || s.config.stations_modelltyp || s.config.stations_seriennummer || s.config.stations_baujahr) && (
        <Card style={{ padding: 18, marginBottom: 16 }}>
          <div style={{ fontWeight: 800, color: C.navy, marginBottom: 10, fontSize: 15 }}>Stationsgebäude</div>
          <FieldRow label='Hersteller' value={s.config.stations_hersteller}/>
          <FieldRow label='Typ / Modell' value={s.config.stations_modelltyp}/>
          <FieldRow label='Seriennummer' value={s.config.stations_seriennummer}/>
          <FieldRow label='Baujahr' value={s.config.stations_baujahr}/>
        </Card>
      )}
      {s.config && s.config.ms_anlage_vorhanden && (
        <Card style={{ padding: 18, marginBottom: 16 }}>
          <div style={{ fontWeight: 800, color: C.navy, marginBottom: 10, fontSize: 15 }}>MS-Anlage</div>
          <FieldRow label='Hersteller' value={s.config.ms_anlage_hersteller}/>
          <FieldRow label='Anzahl Felder' value={s.config.ms_anlage_anzahl_felder}/>
          <FieldRow label='Betriebsspannung' value={s.config.ms_anlage_betriebsspannung_kv ? s.config.ms_anlage_betriebsspannung_kv + ' kV' : null}/>
          <FieldRow label='Isolierart' value={s.config.ms_anlage_isolierart}/>
          <FieldRow label='Verrechnungsmessung' value={s.config.verrechnungsmessung}/>
        </Card>
      )}
      {s.config && (s.config.erderanlage_vorhanden || s.config.oelauffangwanne_vorhanden || s.config.nshv_vorhanden || s.config.elektrothermografie_durchgef || s.config.stromwandler_vorhanden || s.config.spannungswandler_vorhanden) && (
        <Card style={{ padding: 18, marginBottom: 16 }}>
          <div style={{ fontWeight: 800, color: C.navy, marginBottom: 10, fontSize: 15 }}>Weitere Ausstattung</div>
          <FieldRow label='NSHV' value={s.config.nshv_vorhanden ? 'ja' : 'nein'}/>
          <FieldRow label='Erderanlage' value={s.config.erderanlage_vorhanden ? 'ja' : 'nein'}/>
          <FieldRow label='Ölauffangwanne' value={s.config.oelauffangwanne_vorhanden ? 'ja' : 'nein'}/>
          <FieldRow label='Stromwandler' value={s.config.stromwandler_vorhanden ? 'ja' : 'nein'}/>
          <FieldRow label='Spannungswandler' value={s.config.spannungswandler_vorhanden ? 'ja' : 'nein'}/>
          <FieldRow label='Elektrothermografie' value={s.config.elektrothermografie_durchgef ? 'ja' : 'nein'}/>
        </Card>
      )}
      <Card style={{ padding: 18, marginBottom: 16 }}>
        <div style={{ fontWeight: 800, color: C.navy, marginBottom: 10, fontSize: 15 }}>MS-Felder ({s.msFelder.length})</div>
        {s.msFelder.length === 0 ? <div style={{ color: C.gray500, fontSize: 15 }}>Noch keine MS-Felder erfasst.</div> : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {s.msFelder.map(f => (
              <div key={f.id} style={{ background: C.gray100, padding: '8px 12px', borderRadius: 8, fontSize: 15, color: C.navy, display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ background: C.navy, color: C.yellow, padding: '2px 8px', borderRadius: 5, fontWeight: 800, fontSize: 13 }}>{MS_FELD_KURZ[f.typ_code] || f.typ_code || '?'}</span>
                <span style={{ fontWeight: 700 }}>{f.bezeichnung || f.feld_nr || 'Feld ' + (f.position + 1)}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
      <Card style={{ padding: 18, marginBottom: 16 }}>
        <div style={{ fontWeight: 800, color: C.navy, marginBottom: 10, fontSize: 15 }}>Transformatoren ({s.trafos.length})</div>
        {s.trafos.length === 0 ? <div style={{ color: C.gray500, fontSize: 15 }}>Noch kein Trafo erfasst.</div> : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {s.trafos.map(t => (
              <div key={t.id} style={{ padding: 12, background: C.gray100, borderRadius: 8 }}>
                <div style={{ fontWeight: 700, color: C.navy, marginBottom: 4 }}>{t.bezeichnung || 'Trafo ' + (t.position + 1)}{t.trafoart && <span style={{ marginLeft: 8, fontSize: 13, fontWeight: 600, color: C.gray500 }}>- {t.trafoart}</span>}</div>
                <div style={{ fontSize: 14, color: C.gray700 }}>{t.hersteller || '-'} - {t.leistung_kva ? t.leistung_kva + ' kVA' : '-'}{t.herstellungsjahr ? ' - BJ ' + t.herstellungsjahr : ''}</div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

export default function StammdatenPage({ onBack, initialTab }) {
  const { isAdmin, user } = useAuth();
  const [tab, setTab] = useState(initialTab || 'kunden');
  const [search, setSearch] = useState('');
  const [selectedKundeId, setSelectedKundeId] = useState(null);
  const [selectedStationId, setSelectedStationId] = useState(null);
  const [creatingStationForKunde, setCreatingStationForKunde] = useState(null);
  const [editingStationId, setEditingStationId] = useState(null);
  const [activeProtokollId, setActiveProtokollId] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [creatingProtokoll, setCreatingProtokoll] = useState(false);
  const [editingKundeId, setEditingKundeId] = useState(null);
  const [creatingKunde, setCreatingKunde] = useState(false);

  const handleEditCancel = () => setEditingStationId(null);
  const handleEditSaved = (savedId) => { setEditingStationId(null); setSelectedStationId(savedId); setReloadKey(k => k + 1); };
  const handleCreateCancel = () => setCreatingStationForKunde(null);
  const handleCreateSaved = (newId) => { setCreatingStationForKunde(null); setSelectedStationId(newId); };
  const handleProtokollBack = () => { setActiveProtokollId(null); setReloadKey(k => k + 1); };

  if (activeProtokollId) {
    return <ProtokollFormPage protokollId={activeProtokollId} onBack={handleProtokollBack} />;
  }
  if (editingStationId) {
    return <StationFormPage stationId={editingStationId} onCancel={handleEditCancel} onSaved={handleEditSaved} />;
  }
  if (creatingStationForKunde) {
    return <StationFormPage kundeId={creatingStationForKunde} onCancel={handleCreateCancel} onSaved={handleCreateSaved} />;
  }
  if (editingKundeId) {
    return <KundeFormPage kundeId={editingKundeId}
      onCancel={() => setEditingKundeId(null)}
      onSaved={(id) => { setEditingKundeId(null); setSelectedKundeId(id); setReloadKey(k => k + 1); }} />;
  }
  if (creatingKunde) {
    return <KundeFormPage onCancel={() => setCreatingKunde(false)}
      onSaved={(id) => { setCreatingKunde(false); setSelectedKundeId(id); setReloadKey(k => k + 1); }} />;
  }

  const handleNeueWartung = async (stationId) => {
    setCreatingProtokoll(true);
    try {
      // Gibt es schon eine offene oder geplante Wartung? Dann die nehmen.
      // Vorher entstand ein zweites Protokoll neben der Planung des Bueros -
      // und die Planung galt als nie erledigt.
      const { data: offene, error: eOffen } = await withTimeout(
        supabase.from('protokolle').select('id, wartungs_datum')
          .eq('station_id', stationId).in('status', ['vorbereitet', 'zugewiesen', 'entwurf'])
          .order('wartungs_datum', { ascending: true }).limit(1),
        15000, 'Offene Wartung suchen'
      );
      if (eOffen && !window.confirm('Ob es für diese Station schon eine offene Wartung gibt, ließ sich gerade nicht prüfen ('
        + (eOffen.message || 'Fehler') + ').\n\nTrotzdem eine NEUE Wartung anlegen?')) return;
      let schonEntschieden = false;
      if (!eOffen && offene && offene.length > 0) {
        const o = offene[0];
        const wann = o.wartungs_datum ? o.wartungs_datum.split('-').reverse().join('.') : 'ohne Datum';
        // Klar beschriftete Knoepfe - "Abbrechen" legte hier frueher eine NEUE Wartung an.
        const wahl = await frage({
          titel: 'Offene Wartung vorhanden',
          text: 'Für diese Station gibt es schon eine offene Wartung (' + wann + ').',
          knoepfe: [
            { wert: 'oeffnen', text: 'Vorhandene Wartung öffnen', art: 'haupt' },
            { wert: 'neu', text: 'Trotzdem eine neue anlegen' },
          ],
        });
        if (wahl === 'oeffnen') { setActiveProtokollId(o.id); return; }
        if (wahl !== 'neu') return;
        schonEntschieden = true;
      }
      if (!schonEntschieden && !window.confirm('Neues Wartungsprotokoll für diese Station anlegen?')) return;
      const newId = await createProtokollForStation(stationId, user?.id);
      setActiveProtokollId(newId);
    } catch (e) {
      alert('Fehler beim Anlegen: ' + e.message);
    } finally {
      setCreatingProtokoll(false);
    }
  };

  const handleDeleteStation = async (stationId) => {
    try {
      const { error } = await db.stationen.delete(stationId);
      if (error) { alert('Löschen fehlgeschlagen: ' + error.message); return; }
      setSelectedStationId(null);
      setReloadKey(k => k + 1);
    } catch (e) { alert('Fehler: ' + e.message); }
  };

  const showDetail = selectedKundeId || selectedStationId;

  return (
    <div style={{ background: C.bg, minHeight: '100vh', fontFamily: "system-ui,-apple-system,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif" }}>
      <div style={{ background: C.navy, padding: '14px 14px', boxShadow: '0 2px 14px rgba(0,0,0,0.22)' }}>
        <div style={{ maxWidth: 1400, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 12 }}>
          {onBack && <button onClick={onBack} style={zurueckStil}>‹ Zurück</button>}
          <div style={{ color: C.white, fontWeight: 800, fontSize: 16 }}>Stammdaten</div>
        </div>
      </div>
      {creatingProtokoll && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.7)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: C.white, padding: 30, borderRadius: 12, color: C.navy, fontWeight: 700 }}>Lege neues Protokoll an...</div>
        </div>
      )}
      <div style={{ maxWidth: 1400, margin: '0 auto', padding: '16px 14px' }}>
        {showDetail ? (
          selectedStationId ? (
            <StationDetail key={'station-' + selectedStationId + '-' + reloadKey} stationId={selectedStationId}
              onBack={() => setSelectedStationId(null)} onEdit={(id) => setEditingStationId(id)}
              onDelete={handleDeleteStation} onNeueWartung={handleNeueWartung}
              onOpenProtokoll={(pid) => setActiveProtokollId(pid)} isAdmin={isAdmin}/>
          ) : (
            <KundeDetail kundeId={selectedKundeId} onBack={() => setSelectedKundeId(null)}
              onSelectStation={(id) => setSelectedStationId(id)} onAddStation={(kid) => setCreatingStationForKunde(kid)}
              onEditKunde={(id) => setEditingKundeId(id)}/>
          )
        ) : (
          <>
            <Card style={{ marginBottom: 12, overflow: 'hidden' }}>
              <div style={{ display: 'flex' }}>
                <TabButton active={tab === 'kunden'} onClick={() => { setTab('kunden'); setSearch(''); }}>Kunden</TabButton>
                <TabButton active={tab === 'stationen'} onClick={() => { setTab('stationen'); setSearch(''); }}>Stationen</TabButton>
              </div>
            </Card>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 220px', minWidth: 220 }}>
                <SearchBar value={search} onChange={setSearch} placeholder={tab === 'kunden' ? 'Suchen: Name, Kundennummer, Ort, Ansprechpartner...' : 'Suchen: Bezeichnung, Stationsnummer, Ort...'}/>
              </div>
              {tab === 'kunden' && (
                <button onClick={() => setCreatingKunde(true)} style={{ padding: '11px 16px', background: C.yellow, color: C.navy, border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 800, fontSize: 15, whiteSpace: 'nowrap', flex: '0 0 auto' }}>+ Neuer Kunde</button>
              )}
            </div>
            {tab === 'kunden' ? <KundenList search={search} onSelect={(id) => setSelectedKundeId(id)}/> : <StationenList search={search} onSelect={(id) => setSelectedStationId(id)}/>}
          </>
        )}
      </div>
    </div>
  );
}
